import { useCallback } from "react";
import { JSONSchema7 } from "json-schema";
import { v4 as uuidv4 } from "uuid";
import { buildSkillSlug, Skill } from "@/v2/entities/skill/Skill.model";
import {
  CurlRequest,
  unwrapMangledJsonBody,
  useCurlRequestTransformer,
} from "@/v2/entities/curl-request";

const DATA_FLAG_RE =
  /(--data(?:-raw)?|-d)\s+(?<q>['"])(?<body>(?:\\.|(?!\k<q>).)*)\k<q>/i;

function convertOpenApiPathParams(segment: string): string {
  // OpenAPI path names: ticketId, ticket.id, user-id. Do not use [^}]+; that
  // swallows JSON objects like {"account_id":"..."}.
  return segment.replace(/(?<!\$)\{([A-Za-z_][\w.-]*)\}/g, "${$1}");
}

/**
 * Replace values in the cURL's JSON body with placeholders from the schema.
 * Also parameterizes selected headers and converts URL path parameters to the correct format.
 */
export function replaceArgsWithPlaceholders(
  curl: string,
  schema: JSONSchema7
): string {
  // Replace header values (example: x-tenant-id → $tenant_id)
  const HEADER_PLACEHOLDERS: Record<string, string> = {
    // "x-tenant-id": "tenant_id",
  };

  curl = curl.replace(
    /(-H\s+)(['"])([^'"]+)\2/g,
    (full, prefix, quote, headerLine) => {
      const idx = headerLine.indexOf(":");
      if (idx === -1) return full;
      const name = headerLine.slice(0, idx).trim();
      const key = name.toLowerCase();
      if (HEADER_PLACEHOLDERS[key]) {
        const placeholder = `$${HEADER_PLACEHOLDERS[key]}`;
        return `${prefix}${quote}${name}: ${placeholder}${quote}`;
      }
      return full;
    }
  );

  // Convert {param} → ${param} only outside --data-raw. A global \{([^}]+)\}
  // match treats a JSON object as one path param and saves `${{"account_id":...}}`.
  const dataMatch = curl.match(DATA_FLAG_RE);
  if (dataMatch?.index != null) {
    curl =
      convertOpenApiPathParams(curl.slice(0, dataMatch.index)) +
      curl.slice(dataMatch.index);
  } else {
    curl = convertOpenApiPathParams(curl);
  }

  const m = curl.match(DATA_FLAG_RE);
  if (!m?.groups) return curl;

  const quote = m.groups.q!;
  const rawBody = unwrapMangledJsonBody(m.groups.body!);

  const parsed = tryParseJsonLoosely(rawBody);
  if (!parsed) return curl;

  const replacedObj = replaceWithPlaceholders(parsed, schema);
  const templated = JSON.stringify(replacedObj);

  return curl.replace(DATA_FLAG_RE, `${m[1]} ${quote}${templated}${quote}`);
}

/**
 * Walk the parsed JSON against schema and replace values with placeholders.
 */
function replaceWithPlaceholders(
  obj: any,
  schema: JSONSchema7,
  path: string[] = []
): any {
  const t = schema.type;
  if (
    t === "string" ||
    t === "number" ||
    t === "boolean" ||
    t === "null" ||
    t === "integer"
  ) {
    return `$${path[path.length - 1] ?? "value"}`;
  }
  if (
    t === "object" &&
    schema.properties &&
    typeof obj === "object" &&
    !Array.isArray(obj)
  ) {
    const out: Record<string, any> = {};
    for (const [k, v] of Object.entries(obj)) {
      const subSchema = (schema.properties?.[k] as JSONSchema7) ?? {};
      out[k] = replaceWithPlaceholders(v, subSchema, [...path, k]);
    }
    return out;
  }
  if (t === "array" && schema.items && Array.isArray(obj)) {
    // For simplicity: replace whole array with placeholder
    return `$${path[path.length - 1] ?? "array"}`;
  }
  // fallback
  return `$${path[path.length - 1] ?? "value"}`;
}

function tryParseJsonLoosely(s: string): any | null {
  try {
    return JSON.parse(unwrapMangledJsonBody(s));
  } catch {
    try {
      const maybe = unwrapMangledJsonBody(s)
        .replace(/'([\w$-]+)'\s*:/g, `"$1":`)
        .replace(/:\s*'([^']*)'/g, (_m, v) => `: "${v.replace(/"/g, '\\"')}"`);
      return JSON.parse(maybe);
    } catch {
      return null;
    }
  }
}

export function useBuildSkillForSimulator() {
  const { buildCurl } = useCurlRequestTransformer();

  return useCallback(
    (curlRequest: CurlRequest, bodyParamsSchema?: JSONSchema7): Skill => {
      const curl = buildCurl(curlRequest);
      const skill: Skill = {
        id: uuidv4(),
        name: "Create a scorecard",
        description: "Use it when the user wants to create a scorecard",
        curl: curl,
        slug: buildSkillSlug("create-scorecard"),
        quackUsage: 0,
        requiresApproval: false,
      };

      if (bodyParamsSchema != undefined) {
        const parameterizedCurl = replaceArgsWithPlaceholders(
          curl,
          bodyParamsSchema
        );
        skill.curl = parameterizedCurl;
        // skill.parameters = bodyParamsSchema;
      }

      return skill;
    },
    [buildCurl]
  );
}
